import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { Subscription } from 'rxjs';

import { ThemeService } from '../../shared-services/theme.service';
import { GOOGLE_FONT_CATALOG, googleFontStack } from '../../shared-services/google-fonts.catalog';
import { LayoutPreset, LayoutService } from '../../shared-services/layout.service';
import { SlideItem, SlideshowService } from '../../shared-services/slideshow.service';
import {
  CustomPreset,
  DeepPartial,
  Theme,
  ThemeManifestEntry,
} from '../../shared-services/theme.types';

type FontChoice = {
  label: string;
  stack: string;
};

const FONT_CHOICES: FontChoice[] = [
  { label: 'Inter (Default)', stack: "'Inter', -apple-system, sans-serif" },
  { label: 'Arial', stack: "'Arial', Helvetica, sans-serif" },
  { label: 'Roboto', stack: "'Roboto', 'Helvetica Neue', Arial, sans-serif" },
  { label: 'Georgia', stack: "'Georgia', 'Times New Roman', serif" },
  { label: 'Courier New', stack: "'Courier New', Courier, monospace" },
  { label: 'JetBrains Mono', stack: "'JetBrains Mono', monospace" },
  // Then every Google font (as in Figma); each loads when picked.
  ...GOOGLE_FONT_CATALOG
    .filter(([family]) => !['Inter', 'Roboto', 'JetBrains Mono'].includes(family))
    .map((entry) => ({ label: entry[0], stack: googleFontStack(entry) })),
];

type RadiusChoice = {
  label: string;
  value: string;
};

const RADIUS_CHOICES: RadiusChoice[] = [
  { label: 'None (square)', value: '0px' },
  { label: 'Small (4px)', value: '4px' },
  { label: 'Medium (8px) — Default', value: '8px' },
  { label: 'Large (12px)', value: '12px' },
  { label: 'Extra Large (16px)', value: '16px' },
  { label: 'Pill (24px)', value: '24px' },
];

/**
 * Card style preset — semantic choice that maps to a {shadow, borderColor}
 * pair. From the theme-customization design doc (§3.8 Components).
 */
type CardStyle = 'flat' | 'elevated' | 'bordered';

interface CardStyleChoice {
  label: string;
  value: CardStyle;
  shadow: string;
  borderColor: string;
}

const CARD_STYLE_CHOICES: CardStyleChoice[] = [
  {
    label: 'Flat (no shadow)',
    value: 'flat',
    shadow: 'none',
    borderColor: 'transparent',
  },
  {
    label: 'Elevated (shadow) — Default',
    value: 'elevated',
    shadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    borderColor: 'transparent',
  },
  {
    label: 'Bordered (visible border)',
    value: 'bordered',
    shadow: 'none',
    borderColor: 'rgba(0, 0, 0, 0.15)',
  },
];

/** Button shape — drives --dvm-btn-border-radius + --dvm-filter-radius. */
type ButtonShape = 'square' | 'rounded' | 'pill';

interface ButtonShapeChoice {
  label: string;
  value: ButtonShape;
  borderRadius: string;
}

const BUTTON_SHAPE_CHOICES: ButtonShapeChoice[] = [
  { label: 'Square', value: 'square', borderRadius: '0px' },
  { label: 'Rounded — Default', value: 'rounded', borderRadius: '8px' },
  { label: 'Pill', value: 'pill', borderRadius: '999px' },
];

/**
 * Generic spacing semantic — used for both widget gap and card padding.
 * Each choice carries a concrete CSS length so the saved preset is
 * round-trippable (we reverse-map the px back to the dropdown value).
 */
interface SpacingChoice {
  label: string;
  value: 'compact' | 'comfortable' | 'spacious';
  px: string;
}

const WIDGET_SPACING_CHOICES: SpacingChoice[] = [
  { label: 'Compact (8px)', value: 'compact', px: '8px' },
  { label: 'Comfortable (16px) — Default', value: 'comfortable', px: '16px' },
  { label: 'Spacious (24px)', value: 'spacious', px: '24px' },
];

const CARD_PADDING_CHOICES: SpacingChoice[] = [
  { label: 'Compact (8px)', value: 'compact', px: '8px' },
  { label: 'Comfortable (12px) — Default', value: 'comfortable', px: '12px' },
  { label: 'Spacious (20px)', value: 'spacious', px: '20px' },
];

@Component({
  selector: 'app-theme-selector',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatTabsModule,
  ],
  templateUrl: './theme-selector.component.html',
  styleUrl: './theme-selector.component.scss',
})
export class ThemeSelectorComponent implements OnInit, AfterViewInit, OnDestroy {
  private themeService = inject(ThemeService);
  private layoutService = inject(LayoutService);
  private slideshowService = inject(SlideshowService);
  private dialogRef = inject(MatDialogRef<ThemeSelectorComponent>);

  @ViewChild('previewPane') previewPane!: ElementRef<HTMLElement>;

  // ── Templates tab ──
  themes: ThemeManifestEntry[] = [];
  selectedId: string | null = null;
  activeId: string | null = null;

  // ── Custom tab ──
  fontChoices = FONT_CHOICES;
  radiusChoices = RADIUS_CHOICES;
  cardStyleChoices = CARD_STYLE_CHOICES;
  buttonShapeChoices = BUTTON_SHAPE_CHOICES;
  widgetSpacingChoices = WIDGET_SPACING_CHOICES;
  cardPaddingChoices = CARD_PADDING_CHOICES;
  custom = {
    sidebarBg: '#1F4E79',
    toolbarBg: '#1F4E79',
    contentBg: '#F7F9FC',
    cardHeaderBg: '#F4F7FB',
    contentText: '#0A0A0A',
    /** Data URL for the uploaded dashboard bg image, or empty string for none. */
    contentBgImageDataUrl: '',
    fontFamily: FONT_CHOICES[0].stack,
    cardBorderRadius: RADIUS_CHOICES[2].value, // 8px (default)
    cardStyle: 'elevated' as CardStyle,
    buttonShape: 'rounded' as ButtonShape,
    buttonColor: '#1F4E79',
    widgetSpacing: 'comfortable' as SpacingChoice['value'],
    cardPadding: 'comfortable' as SpacingChoice['value'],
    /**
     * Layout id paired with this custom preset. Empty string `''` means
     * "no layout pairing" (preset stays layout-agnostic — current layout
     * is left untouched on apply). The dropdown's first option binds to ''.
     * On preset upsert, '' becomes `undefined` on the saved CustomPreset.
     */
    layoutId: '' as string,
  };

  /** Soft warning shown when an uploaded image is large enough to risk
   * exceeding the localStorage quota when persisted. */
  bgImageWarning = '';

  // ── Custom Layout Templates tab ──
  layouts: LayoutPreset[] = [];
  selectedLayoutId: string | null = null;
  activeLayoutId: string | null = null;

  // ── Slideshow tab ──
  // All slideshow state lives in SlideshowService so the dashboard toolbar
  // can keep playing after the dialog closes. The component just mirrors
  // the service state into local fields via a subscription, plus reads
  // service.state for the few synchronous getters the template binds.
  slideshowIntervalMs = 3000;
  private slideshowSub?: Subscription;

  // ── Shared preview / tab tracking ──
  activeTabIndex = 0;
  previewPalette: string[] = [];
  previewThemeName = '';

  /** All saved user-created custom presets (Custom 1, Custom 2, …). */
  customPresets: CustomPreset[] = [];

  /**
   * Id of the preset currently being edited in the Custom tab.
   * `null` means "new" mode (inputs reset to white/black, heading reads
   * "New Custom Template"); a preset id means "edit" mode (inputs prefilled
   * from that preset, heading reads "Edit Custom Template").
   */
  editingPresetId: string | null = null;

  /**
   * Set transiently by `editCustomPreset()` to suppress the auto-reset that
   * fires when the Custom tab becomes active. Without this, opening Custom
   * via the Edit button would immediately wipe the prefilled inputs.
   */
  private suppressNextCustomReset = false;

  private sub?: Subscription;
  private layoutSub?: Subscription;

  ngOnInit(): void {
    // Highlight whichever preset is in effect: a custom preset (if one was
    // applied) takes precedence over the underlying base template name. So
    // applying "Custom 1" highlights the Custom 1 card, not its baseTemplate.
    this.activeId =
      this.themeService.activePresetId || this.themeService.currentThemeName;
    this.selectedId = this.activeId;
    this.customPresets = this.themeService.getCustomPresets();

    const cached = this.themeService.getManifest();
    if (cached) {
      this.themes = cached.themes;
    } else {
      this.sub = this.themeService.loadManifest().subscribe((m) => {
        this.themes = m.themes;
      });
    }

    // Layout tab: hydrate active id from the LayoutService and pre-select it
    // so the matching card is highlighted when the user lands on the tab.
    this.activeLayoutId = this.layoutService.getActiveLayoutId();
    this.selectedLayoutId = this.activeLayoutId;
    const cachedLayouts = this.layoutService.getManifest();
    if (cachedLayouts) {
      this.layouts = cachedLayouts.layouts;
    } else {
      this.layoutSub = this.layoutService.loadManifest().subscribe((m) => {
        this.layouts = m.layouts;
      });
    }

    // If a slideshow is already running (the user opened the dialog while it
    // was playing in the toolbar), pre-select the Slideshow tab and pull the
    // current speed so the dropdown reflects reality. Otherwise the dropdown
    // shows the default 3s — fine for the first run.
    this.slideshowSub = this.slideshowService.state$.subscribe((state) => {
      this.slideshowIntervalMs = state.intervalMs;
      if (state.active && this.activeTabIndex === 0) {
        this.activeTabIndex = 4;
      }
    });
  }

  /** Mat-tab-group `(selectedTabChange)` handler — when the user lands on
   * the Custom tab via the tab label (not via Edit), reset inputs so they
   * can build a brand-new custom from a clean white/black baseline. The
   * slideshow keeps running (if active) regardless of the active tab; the
   * toolbar surfaces controls so users don't need to stay on this tab. */
  onTabChange(event: { index: number }): void {
    if (event.index !== 2) return;
    if (this.suppressNextCustomReset) {
      this.suppressNextCustomReset = false;
      return;
    }
    this.startNewCustom();
  }

  /** Switch the Custom tab into "new" mode: reset inputs + clear editing id. */
  startNewCustom(): void {
    this.editingPresetId = null;
    this.resetCustomInputs();
  }

  /** Open the Custom tab in "edit" mode for a specific preset. */
  editCustomPreset(preset: CustomPreset, event?: Event): void {
    if (event) event.stopPropagation();
    this.editingPresetId = preset.id;
    this.seedCustomFromPreset(preset);
    this.suppressNextCustomReset = true;
    this.activeTabIndex = 2;
  }

  /** Heading shown above the Custom-tab inputs. */
  get customSectionHeading(): string {
    return this.editingPresetId ? 'Edit Custom Template' : 'New Custom Template';
  }

  /** True when the currently-selected card on the Templates tab is one of
   * the user's custom presets — drives the visibility of the Edit button. */
  get selectedCustomPreset(): CustomPreset | null {
    if (!this.selectedId) return null;
    return this.customPresets.find((p) => p.id === this.selectedId) || null;
  }

  /** Three preview swatches derived from a preset's overrides. */
  presetSwatches(preset: CustomPreset): string[] {
    return [
      (preset.overrides.layout?.sidebar?.bg as string) || '#FFFFFF',
      (preset.overrides.layout?.content?.bg as string) || '#FFFFFF',
      (preset.overrides.card?.headerBg as string) || '#FFFFFF',
    ];
  }

  // ── Custom-tab seeding & reset ─────────────────────────────────

  protected resetCustomInputs(): void {
    this.custom = {
      sidebarBg: '#FFFFFF',
      toolbarBg: '#FFFFFF',
      contentBg: '#FFFFFF',
      cardHeaderBg: '#FFFFFF',
      contentText: '#000000',
      contentBgImageDataUrl: '',
      fontFamily: FONT_CHOICES[0].stack,
      cardBorderRadius: RADIUS_CHOICES[2].value,
      cardStyle: 'elevated',
      buttonShape: 'rounded',
      buttonColor: '#1F4E79',
      widgetSpacing: 'comfortable',
      cardPadding: 'comfortable',
      layoutId: '',
    };
    this.bgImageWarning = '';
  }

  protected seedCustomFromPreset(preset: CustomPreset): void {
    const o = preset.overrides;
    this.custom.sidebarBg = (o.layout?.sidebar?.bg as string) || '#FFFFFF';
    this.custom.toolbarBg = (o.layout?.toolbar?.bg as string) || '#FFFFFF';
    this.custom.contentBg = (o.layout?.content?.bg as string) || '#FFFFFF';
    this.custom.contentText = (o.layout?.content?.text as string) || '#000000';
    this.custom.cardHeaderBg = (o.card?.headerBg as string) || '#FFFFFF';
    this.custom.cardBorderRadius =
      (o.card?.borderRadius as string) || RADIUS_CHOICES[2].value;
    this.custom.fontFamily = (o.fontFamily as string) || FONT_CHOICES[0].stack;
    // Reverse-map the saved shadow/borderColor back to a semantic CardStyle
    // so the dropdown reflects what the user picked (elevated by default).
    const savedShadow = o.card?.shadow as string | undefined;
    const savedBorder = o.card?.borderColor as string | undefined;
    const matched = CARD_STYLE_CHOICES.find(
      (c) => c.shadow === savedShadow && c.borderColor === savedBorder,
    );
    this.custom.cardStyle = matched ? matched.value : 'elevated';

    const savedBgImage = o.layout?.content?.bgImage as string | undefined;
    if (savedBgImage && savedBgImage !== 'none') {
      const match = savedBgImage.match(/^url\(["']?(.+?)["']?\)$/);
      this.custom.contentBgImageDataUrl = match ? match[1] : '';
    } else {
      this.custom.contentBgImageDataUrl = '';
    }
    this.bgImageWarning = '';

    // Reverse-map the saved button-radius / spacing tokens back to the
    // semantic dropdown values so the Edit dialog reflects what the user
    // originally picked. Falls back to the defaults if the saved value
    // doesn't match any choice (e.g. preset migrated from an older shape).
    const savedBtnRadius = o.button?.borderRadius as string | undefined;
    const matchedBtnShape = BUTTON_SHAPE_CHOICES.find(
      (c) => c.borderRadius === savedBtnRadius,
    );
    this.custom.buttonShape = matchedBtnShape ? matchedBtnShape.value : 'rounded';
    this.custom.buttonColor = (o.button?.primaryBg as string) || '#1F4E79';

    const savedWidgetGap = o.widgetGap as string | undefined;
    const matchedGap = WIDGET_SPACING_CHOICES.find((c) => c.px === savedWidgetGap);
    this.custom.widgetSpacing = matchedGap ? matchedGap.value : 'comfortable';

    const savedCardPad = o.card?.padding as string | undefined;
    const matchedPad = CARD_PADDING_CHOICES.find((c) => c.px === savedCardPad);
    this.custom.cardPadding = matchedPad ? matchedPad.value : 'comfortable';

    // Restore the layout pairing from the preset itself (it lives outside
    // `overrides` — the layoutId is a sibling of overrides on CustomPreset).
    // Empty string represents "no pairing"; the dropdown's first option uses
    // that value so {{ select }} can bind to it. Treat null the same as
    // undefined for the dropdown — both render as "No layout pairing"; the
    // null-vs-undefined distinction only matters at apply time.
    this.custom.layoutId = preset.layoutId || '';
  }

  protected nextCustomName(): string {
    const used = this.customPresets
      .map((p) => /^Custom (\d+)$/.exec(p.name))
      .filter((m): m is RegExpExecArray => m !== null)
      .map((m) => parseInt(m[1], 10));
    const max = used.length ? Math.max(...used) : 0;
    return `Custom ${max + 1}`;
  }

  ngAfterViewInit(): void {
    this.seedInitialPreview();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.layoutSub?.unsubscribe();
    this.slideshowSub?.unsubscribe();
    // Note: closing the dialog no longer cancels the slideshow. The whole
    // point of moving state into SlideshowService was to let the toolbar
    // keep cycling themes after the user dismisses the dialog with X / ESC.
    // Apply / Save / Cancel must be triggered explicitly (toolbar or footer).
  }

  // ── Templates tab actions ──

  selectTemplate(id: string): void {
    this.selectedId = id;
  }

  applyPreview(): void {
    if (!this.selectedId) return;
    // If the selected id matches a saved custom preset, apply that preset
    // (its base template + overrides). Otherwise treat it as a built-in
    // template id.
    const preset = this.customPresets.find((p) => p.id === this.selectedId);
    if (preset) {
      this.themeService.applyCustomPresetPreview(preset);
      this.activeId = preset.id;
      this.close();
      return;
    }
    this.themeService.applyTemplatePreview(this.selectedId);
    this.activeId = this.selectedId;
    this.close();
  }

  saveTemplate(): void {
    if (!this.selectedId) return;
    // Picking a preset template is an intent to drop any previously-saved
    // Custom-tab overrides — otherwise the old custom sidebar/card colors
    // would still win over the new template via the merge order in
    // ThemeService.applyGlobalTheme().
    this.themeService.clearCustomOverrides();
    this.themeService.saveTheme(this.selectedId);
    this.activeId = this.selectedId;
    this.close();
  }

  resetAll(): void {
    this.themeService.resetToDefault();
    // "Reset to Default" should also drop *all* saved layouts — not just the
    // active dashboard's. Otherwise other dashboards would keep their
    // per-dashboard picks (and the legacy global) after a "factory reset",
    // which feels inconsistent with the user's intent.
    this.layoutService.clearAllSavedLayouts();
    this.activeLayoutId = null;
    this.selectedLayoutId = null;
    this.close();
  }

  // ── Custom Layout Templates tab actions ──

  selectLayout(id: string): void {
    this.selectedLayoutId = id;
  }

  applyLayoutPreview(): void {
    if (!this.selectedLayoutId) return;
    // Live-apply without persisting — the toolbar Save Theme button (driven
    // by LayoutService.pendingLayout$) commits this when the user is happy.
    this.layoutService.applyLayoutPreview(this.selectedLayoutId);
    this.activeLayoutId = this.selectedLayoutId;
    this.close();
  }

  /** Combined Apply for the merged Theme + Layout tab. Applies whichever is
   *  selected: theme only, layout only, or both. Closes the dialog once at
   *  the end so the underlying apply* methods don't each trigger their own
   *  close (which would no-op after the first). */
  applyCombinedPreview(): void {
    const hasTheme = !!this.selectedId;
    const hasLayout = !!this.selectedLayoutId;
    if (!hasTheme && !hasLayout) return;

    if (hasTheme) {
      const preset = this.customPresets.find((p) => p.id === this.selectedId);
      if (preset) {
        this.themeService.applyCustomPresetPreview(preset);
        this.activeId = preset.id;
      } else {
        this.themeService.applyTemplatePreview(this.selectedId!);
        this.activeId = this.selectedId;
      }
    }

    if (hasLayout) {
      this.layoutService.applyLayoutPreview(this.selectedLayoutId!);
      this.activeLayoutId = this.selectedLayoutId;
    }

    this.close();
  }

  /** Drop any active layout — dashboards revert to their own pageContent. */
  clearLayout(): void {
    this.layoutService.clearActiveLayout();
    this.activeLayoutId = null;
    this.selectedLayoutId = null;
    this.close();
  }

  /** Template-side helper: turn a row count into a fixed-length array so
   * `*ngFor` can render that many wireframe cells in the layout preview. */
  countToArray(count: number): unknown[] {
    return new Array(Math.max(0, count));
  }

  // ── Slideshow tab actions (delegate to SlideshowService) ──

  /**
   * Unified list of every theme the slideshow can cycle through — built-in
   * manifest themes followed by the user's saved custom presets. Each slide
   * is also paired with a layout id from `this.layouts`, cycling by index
   * so a 7-themes × 7-layouts manifest pairs them 1:1 and longer theme
   * lists wrap around the layout list (slide N → layouts[N % layouts.length]).
   * Built on the fly so it always reflects the latest themes/customPresets
   * state (e.g. a preset deleted via the Templates tab disappears).
   */
  get slideshowItems(): SlideItem[] {
    const builtins: SlideItem[] = this.themes.map((t) => ({
      id: t.id,
      name: t.name,
      isCustom: false,
      swatches: [t.preview.primary, t.preview.surface, t.preview.accent],
      type: t.type,
    }));
    const customs: SlideItem[] = this.customPresets.map((p) => ({
      id: p.id,
      name: p.name,
      isCustom: true,
      swatches: this.presetSwatches(p),
      type: 'custom',
    }));
    const all = [...builtins, ...customs];
    const layoutCount = this.layouts.length;
    if (layoutCount === 0) return all;
    return all.map((slide, i) => ({
      ...slide,
      layoutId: this.layouts[i % layoutCount].id,
    }));
  }

  /** Sync read of the service's current cursor — drives the stage card. */
  get slideshowIndex(): number {
    const s = this.slideshowService.state;
    return s.active ? s.index : 0;
  }

  /** Sync read of `playing` — drives the play/pause toggle icon. */
  get slideshowPlaying(): boolean {
    return this.slideshowService.state.playing;
  }

  /** The slide currently on stage. When the slideshow hasn't been started
   *  yet, fall back to the first item so the dialog has something to show
   *  before the user clicks Play. */
  get currentSlide(): SlideItem | null {
    const s = this.slideshowService.state;
    if (s.active && s.current) return s.current;
    return this.slideshowItems[0] || null;
  }

  /** User clicked Play. Hand the items list to the service so it survives
   *  dialog close, then close the dialog so the slideshow plays "in the
   *  dashboard" with toolbar controls — that's the whole UX point. If the
   *  service is already active, just resume (e.g. user re-opened dialog
   *  while paused, hit play again). */
  playSlideshow(): void {
    const items = this.slideshowItems;
    if (items.length === 0) return;
    if (this.slideshowService.isActive) {
      this.slideshowService.play();
    } else {
      const startIndex = this.slideshowIndex;
      this.slideshowService.start(items, startIndex, this.slideshowIntervalMs, true);
    }
    this.close();
  }

  pauseSlideshow(): void {
    this.slideshowService.pause();
  }

  nextSlide(): void {
    if (this.slideshowService.isActive) {
      this.slideshowService.next();
      return;
    }
    // Pre-start preview from the dialog: bootstrap the service with items
    // (paused) so prev/next can step through without auto-advancing.
    const items = this.slideshowItems;
    if (!items.length) return;
    const next = (this.slideshowIndex + 1) % items.length;
    this.slideshowService.start(items, next, this.slideshowIntervalMs, false);
  }

  prevSlide(): void {
    if (this.slideshowService.isActive) {
      this.slideshowService.prev();
      return;
    }
    const items = this.slideshowItems;
    if (!items.length) return;
    const prev = (this.slideshowIndex - 1 + items.length) % items.length;
    this.slideshowService.start(items, prev, this.slideshowIntervalMs, false);
  }

  /** Speed dropdown change handler — push to the service. */
  onSlideshowSpeedChange(): void {
    this.slideshowService.setIntervalMs(this.slideshowIntervalMs);
  }

  /** Footer Apply & Preview — delegate to service then close the dialog. */
  applySlideshowTheme(): void {
    this.slideshowService.applyAndStop();
    this.close();
  }

  /** Footer Save — delegate to service then close the dialog. */
  saveSlideshowTheme(): void {
    this.slideshowService.saveAndStop();
    this.close();
  }

  /** Footer Cancel — revert to the theme active before slideshow started. */
  cancelSlideshow(): void {
    this.slideshowService.cancelAndStop();
    this.close();
  }

  // ── Custom tab actions ──

  /**
   * Pick a readable text color (white or near-black) given a background hex.
   * Uses perceived-brightness (Rec. 601 luma) — same algorithm as the dark/
   * light theme detection in ThemeService.
   */
  protected static contrastingText(bgHex: string): string {
    const hex = (bgHex || '').replace('#', '').slice(0, 6);
    if (hex.length !== 6 || !/^[0-9a-f]{6}$/i.test(hex)) return '#0A0A0A';
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const brightness = r * 0.299 + g * 0.587 + b * 0.114;
    return brightness < 128 ? '#FFFFFF' : '#0A0A0A';
  }

  protected buildCustomOverrides(): DeepPartial<Theme> {
    const bgImageCss = this.custom.contentBgImageDataUrl
      ? `url("${this.custom.contentBgImageDataUrl}")`
      : 'none';
    // The single "Text color" picker drives every text token the user can see
    // on the chrome: sidebar nav, toolbar, card headers, page heading, body.
    // This matches the user's expectation that "Text color" applies globally.
    const text = this.custom.contentText;
    // Map the semantic Card Style choice → concrete shadow + border tokens.
    const style =
      CARD_STYLE_CHOICES.find((c) => c.value === this.custom.cardStyle) ||
      CARD_STYLE_CHOICES[1]; // elevated default
    const btnShape =
      BUTTON_SHAPE_CHOICES.find((c) => c.value === this.custom.buttonShape) ||
      BUTTON_SHAPE_CHOICES[1];
    const widgetGap =
      WIDGET_SPACING_CHOICES.find((c) => c.value === this.custom.widgetSpacing) ||
      WIDGET_SPACING_CHOICES[1];
    const cardPad =
      CARD_PADDING_CHOICES.find((c) => c.value === this.custom.cardPadding) ||
      CARD_PADDING_CHOICES[1];
    // Hover bg auto-darkens the chosen primary so users get a visible hover
    // state without exposing yet another picker. Text auto-derived for
    // contrast on the chosen primary.
    const btnHover = ThemeSelectorComponent.darkenHex(this.custom.buttonColor);
    const btnText = ThemeSelectorComponent.contrastingText(this.custom.buttonColor);
    return {
      fontFamily: this.custom.fontFamily,
      widgetGap: widgetGap.px,
      layout: {
        sidebar: { bg: this.custom.sidebarBg, text },
        toolbar: { bg: this.custom.toolbarBg, text },
        content: {
          bg: this.custom.contentBg,
          text,
          headingText: text,
          bgImage: bgImageCss,
        },
      },
      card: {
        headerBg: this.custom.cardHeaderBg,
        // Card header text is auto-derived from headerBg brightness so titles
        // stay readable regardless of the user's "Text color" pick. Without
        // this, picking a dark text + a dark cardHeaderBg (or light + light)
        // would leave card titles, table column headers, the entity-mgmt
        // banner ("010 - Part"), and side-entity-form banner ("Details")
        // invisible — they all read --dvm-card-header-text.
        headerText: ThemeSelectorComponent.contrastingText(this.custom.cardHeaderBg),
        borderRadius: this.custom.cardBorderRadius,
        shadow: style.shadow,
        borderColor: style.borderColor,
        padding: cardPad.px,
      },
      button: {
        primaryBg: this.custom.buttonColor,
        primaryText: btnText,
        primaryHoverBg: btnHover,
        borderRadius: btnShape.borderRadius,
      },
    };
  }

  /**
   * Darken a hex color by the given fraction (0–1). Used to auto-derive a
   * button-hover bg from the user's chosen primary so they don't have to pick
   * one explicitly. Returns the input unchanged for non-hex strings (e.g.
   * named CSS colors, rgba(...)) so we never silently corrupt valid input.
   */
  protected static darkenHex(hex: string, amount = 0.12): string {
    const m = (hex || '').replace('#', '').slice(0, 6);
    if (m.length !== 6 || !/^[0-9a-f]{6}$/i.test(m)) return hex;
    const f = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
    const r = f(parseInt(m.slice(0, 2), 16) * (1 - amount));
    const g = f(parseInt(m.slice(2, 4), 16) * (1 - amount));
    const b = f(parseInt(m.slice(4, 6), 16) * (1 - amount));
    const toHex = (n: number) => n.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  applyCustomPreview(): void {
    // Persist the new/edited preset, then apply it live (preset includes its
    // base template, so we get the same result on reload). Toolbar's pending
    // Save button shows so the user can commit.
    const preset = this.upsertEditingPreset();
    this.themeService.applyCustomPresetPreview(preset);
    this.close();
  }

  saveCustom(): void {
    // Same upsert as Apply & Preview but also commits to localStorage so the
    // theme survives a reload without needing the toolbar Save button.
    const preset = this.upsertEditingPreset();
    this.themeService.applyCustomPresetPreview(preset);
    this.themeService.commitPendingPreview();
    this.close();
  }

  /**
   * Build a CustomPreset from the current Custom-tab inputs and stage it as
   * the in-memory pending preset. NOT persisted to localStorage here — the
   * toolbar Save Theme button (`commitPendingPreview`) is what writes it to
   * the saved presets list. This makes Apply & Preview a true preview that
   * the user can walk away from without leaving a saved Custom 1, Custom 2…
   */
  protected upsertEditingPreset(): CustomPreset {
    const overrides = this.buildCustomOverrides();
    const baseTemplate =
      this.activeId || this.themeService.currentThemeName || 'default';
    // Empty string from the dropdown ⇒ undefined on the saved preset, which
    // ThemeService.applyCustomPresetPreview reads as "leave layout alone".
    // A picked layout id passes through as-is; the user explicitly opted in.
    const layoutId: string | undefined =
      this.custom.layoutId ? this.custom.layoutId : undefined;
    let preset: CustomPreset;
    if (this.editingPresetId) {
      const existing = this.customPresets.find(
        (p) => p.id === this.editingPresetId,
      );
      preset = {
        id: this.editingPresetId,
        name: existing?.name || this.nextCustomName(),
        baseTemplate: existing?.baseTemplate || baseTemplate,
        overrides,
        layoutId,
      };
    } else {
      preset = {
        id: 'custom-' + Date.now(),
        name: this.nextCustomName(),
        baseTemplate,
        overrides,
        layoutId,
      };
    }
    this.themeService.pendingCustomPreset = preset;
    this.customPresets = this.themeService.getCustomPresets();
    this.editingPresetId = preset.id;
    return preset;
  }

  /** Delete a saved custom preset (used by future delete affordance). */
  deleteCustomPreset(preset: CustomPreset, event?: Event): void {
    if (event) event.stopPropagation();
    this.themeService.deleteCustomPreset(preset.id);
    this.customPresets = this.themeService.getCustomPresets();
    if (this.selectedId === preset.id) this.selectedId = this.activeId;
    if (this.editingPresetId === preset.id) this.startNewCustom();
  }

  clearCustom(): void {
    this.themeService.clearCustomOverrides();
    this.close();
  }

  // ── Dialog close ──

  close(): void {
    this.dialogRef.close();
  }

  // ── Preview helpers ──

  /** Seed the preview pane with the currently-active resolved theme. */
  protected seedInitialPreview(): void {
    const active = this.themeService.resolve();
    if (active && active.chart) this.renderPreview(active);
  }

  /** Apply a theme's tokens to the scoped preview pane only (never globally). */
  protected renderPreview(theme: Theme): void {
    if (!this.previewPane?.nativeElement) return;
    this.themeService.applyThemeTokensTo(theme, this.previewPane.nativeElement);
    this.previewPalette = (theme.chart?.palette || []).slice(0, 6);
    this.previewThemeName = theme.name || '';
  }

  protected seedCustomFromCurrent(): void {
    const fallback = FONT_CHOICES[0].stack;
    const saved = this.themeService.getSavedCustomOverrides();
    if (saved) {
      this.custom.sidebarBg = (saved.layout?.sidebar?.bg as string) || this.custom.sidebarBg;
      this.custom.toolbarBg = (saved.layout?.toolbar?.bg as string) || this.custom.toolbarBg;
      this.custom.contentBg = (saved.layout?.content?.bg as string) || this.custom.contentBg;
      this.custom.contentText = (saved.layout?.content?.text as string) || this.custom.contentText;
      this.custom.cardHeaderBg = (saved.card?.headerBg as string) || this.custom.cardHeaderBg;
      this.custom.cardBorderRadius = (saved.card?.borderRadius as string) || this.custom.cardBorderRadius;
      this.custom.fontFamily = (saved.fontFamily as string) || fallback;

      // Saved bgImage is stored as `url("data:...")` — strip the wrapper to
      // recover the raw data URL for the file-input preview.
      const savedBgImage = saved.layout?.content?.bgImage as string | undefined;
      if (savedBgImage && savedBgImage !== 'none') {
        const match = savedBgImage.match(/^url\(["']?(.+?)["']?\)$/);
        this.custom.contentBgImageDataUrl = match ? match[1] : '';
      }
    }
  }

  // ── Background image upload ─────────────────────────────────────

  /** Approx. localStorage byte budget warning threshold for the image. */
  private static readonly IMAGE_WARN_BYTES = 800_000; // ~800KB raw → ~1.1MB base64

  onBackgroundImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.bgImageWarning = 'Please choose an image file.';
      return;
    }
    if (file.size > ThemeSelectorComponent.IMAGE_WARN_BYTES) {
      this.bgImageWarning =
        'Large image — saving may exceed localStorage quota. Consider a smaller file.';
    } else {
      this.bgImageWarning = '';
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      this.custom.contentBgImageDataUrl = dataUrl;
      // Reset the file input so the same file can be re-selected later.
      input.value = '';
    };
    reader.onerror = () => {
      this.bgImageWarning = 'Failed to read the selected image.';
    };
    reader.readAsDataURL(file);
  }

  removeBackgroundImage(): void {
    this.custom.contentBgImageDataUrl = '';
    this.bgImageWarning = '';
  }
}
